import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import {
  CheckCircle2, Clock, Folder, Inbox, LogOut, MessageCircle, Phone, Trash2, TrendingUp,
} from "lucide-react";
import { toast } from "sonner";
import { Logo } from "../components/Logo";
import { ProjectManager } from "../components/ProjectManager";
import {
  adminContacts, adminLeads, adminLogout, adminStats, deleteContact, deleteLead, updateLeadStatus,
} from "../lib/api";

const STATUSES = ["new", "contacted", "converted", "closed"];

export default function AdminDashboard() {
  const [tab, setTab] = useState("leads");
  const [leads, setLeads] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();
  const hasToken = typeof window !== "undefined" && !!localStorage.getItem("monarch_token");

  const refreshStats = useCallback(async () => {
    try {
      const s = await adminStats();
      setStats(s);
    } catch (err) {
      console.error("Failed to refresh stats:", err);
    }
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [l, c, s] = await Promise.all([adminLeads(), adminContacts(), adminStats()]);
      setLeads(l);
      setContacts(c);
      setStats(s);
    } catch (err) {
      if (err?.response?.status === 401) {
        adminLogout();
        nav("/admin/login");
      } else {
        toast.error("Failed to load data");
      }
    } finally {
      setLoading(false);
    }
  }, [nav]);

  useEffect(() => {
    if (hasToken) refresh();
  }, [hasToken, refresh]);

  if (!hasToken) {
    return <Navigate to="/admin/login" replace />;
  }

  const onLogout = () => {
    adminLogout();
    nav("/admin/login");
  };

  const onStatus = async (id, status) => {
    try {
      const updated = await updateLeadStatus(id, status);
      setLeads((arr) => arr.map((l) => (l.id === id ? updated : l)));
      toast.success(`Marked as ${status}`);
      const s = await adminStats();
      setStats(s);
    } catch { toast.error("Update failed"); }
  };

  const onDeleteLead = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    try {
      await deleteLead(id);
      setLeads((arr) => arr.filter((l) => l.id !== id));
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  };

  const onDeleteContact = async (id) => {
    if (!window.confirm("Delete this contact?")) return;
    try {
      await deleteContact(id);
      setContacts((arr) => arr.filter((c) => c.id !== id));
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  };

  return (
    <main className="min-h-screen bg-[var(--m-bg-2)]" data-testid="admin-dashboard">
      <header className="bg-[var(--m-bg)] border-b border-[var(--m-border)] sticky top-0 z-30">
        <div className="container-x py-4 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/"><Logo compact /></Link>
            <span className="hidden md:inline text-xs tracking-[0.22em] uppercase text-[var(--m-ink-3)]">Admin Console</span>
          </div>
          <button onClick={onLogout} className="btn-secondary" data-testid="admin-logout">
            <LogOut size={14} /> Logout
          </button>
        </div>
      </header>

      <div className="container-x py-12">
        {/* STATS */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-10">
          <StatCard icon={Inbox} label="Total Leads" value={stats?.total_leads ?? "—"} testId="stat-total-leads" />
          <StatCard icon={Clock} label="New / Pending" value={stats?.new_leads ?? "—"} testId="stat-new-leads" />
          <StatCard icon={TrendingUp} label="Converted" value={stats?.converted_leads ?? "—"} testId="stat-converted" />
          <StatCard icon={MessageCircle} label="Contact Messages" value={stats?.total_contacts ?? "—"} testId="stat-contacts" />
          <StatCard icon={Folder} label="Projects" value={stats?.total_projects ?? "—"} testId="stat-projects" />
        </div>

        {/* TABS */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {[
            { k: "leads", label: `Quotation Leads (${leads.length})` },
            { k: "contacts", label: `Contact Messages (${contacts.length})` },
            { k: "projects", label: "Projects & Media" },
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              className="px-6 py-3 text-xs tracking-[0.18em] uppercase border"
              style={{
                background: tab === t.k ? "var(--m-ink)" : "var(--m-bg)",
                color: tab === t.k ? "var(--m-bg)" : "var(--m-ink)",
                borderColor: tab === t.k ? "var(--m-ink)" : "var(--m-border)",
              }}
              data-testid={`admin-tab-${t.k}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* CONTENT */}
        {loading ? (
          <div className="bg-[var(--m-bg)] border border-[var(--m-border)] p-20 text-center text-[var(--m-ink-3)]">Loading...</div>
        ) : tab === "leads" ? (
          <LeadsTable leads={leads} onStatus={onStatus} onDelete={onDeleteLead} />
        ) : tab === "contacts" ? (
          <ContactsTable contacts={contacts} onDelete={onDeleteContact} />
        ) : (
          <ProjectManager onCountChange={refreshStats} />
        )}
      </div>
    </main>
  );
}

const StatCard = ({ icon: Icon, label, value, testId }) => (
  <div className="bg-[var(--m-bg)] border border-[var(--m-border)] p-6" data-testid={testId}>
    <Icon size={20} className="text-[var(--m-teal)] mb-3" strokeWidth={1.5} />
    <div className="font-display text-4xl">{value}</div>
    <div className="text-[10px] tracking-[0.22em] uppercase text-[var(--m-ink-3)] mt-2">{label}</div>
  </div>
);

const StatusBadge = ({ s }) => {
  const colors = {
    new: { bg: "rgba(31,78,91,0.08)", color: "var(--m-teal)", border: "var(--m-teal)" },
    contacted: { bg: "rgba(184,134,11,0.08)", color: "var(--m-warning)", border: "var(--m-warning)" },
    converted: { bg: "rgba(45,106,79,0.08)", color: "var(--m-success)", border: "var(--m-success)" },
    closed: { bg: "rgba(122,125,132,0.08)", color: "var(--m-ink-3)", border: "var(--m-border)" },
  }[s] || {};
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] tracking-[0.16em] uppercase font-medium border"
          style={{ background: colors.bg, color: colors.color, borderColor: colors.border }}>
      <CheckCircle2 size={11} />
      {s}
    </span>
  );
};

const LeadsTable = ({ leads, onStatus, onDelete }) => {
  if (leads.length === 0) {
    return <div className="bg-[var(--m-bg)] border border-[var(--m-border)] p-20 text-center text-[var(--m-ink-3)]">No leads yet. Submissions from the Quotation page will appear here.</div>;
  }
  return (
    <div className="bg-[var(--m-bg)] border border-[var(--m-border)] overflow-x-auto" data-testid="admin-leads-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[var(--m-border)] text-left">
            {["Name", "Phone / WhatsApp", "Project", "Plot / Location", "Package", "Budget", "Status", "When", ""].map((h) => (
              <th key={h} className="px-4 py-4 text-[10px] tracking-[0.18em] uppercase text-[var(--m-ink-3)] font-medium whitespace-nowrap">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {leads.map((l) => (
            <tr key={l.id} className="border-b border-[var(--m-border)] hover:bg-[var(--m-bg-2)]" data-testid={`lead-row-${l.id}`}>
              <td className="px-4 py-4 align-top">
                <div className="font-semibold">{l.name}</div>
                {l.email && <div className="text-xs text-[var(--m-ink-3)] mt-0.5">{l.email}</div>}
                {l.message && <div className="text-xs text-[var(--m-ink-2)] mt-2 max-w-xs italic">"{l.message}"</div>}
              </td>
              <td className="px-4 py-4 align-top whitespace-nowrap">
                <a href={`tel:${l.phone}`} className="flex items-center gap-1 hover:text-[var(--m-teal)]"><Phone size={11} /> {l.phone}</a>
                {l.whatsapp && (
                  <a href={`https://wa.me/${l.whatsapp.replace(/\D/g, "")}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 mt-1 text-xs text-[#25D366]">
                    <MessageCircle size={11} /> {l.whatsapp}
                  </a>
                )}
              </td>
              <td className="px-4 py-4 align-top">{l.project_type || "—"}</td>
              <td className="px-4 py-4 align-top">
                <div>{l.plot_size || "—"}</div>
                <div className="text-xs text-[var(--m-ink-3)] mt-0.5">{l.location || ""}</div>
              </td>
              <td className="px-4 py-4 align-top">{l.package || "—"}</td>
              <td className="px-4 py-4 align-top whitespace-nowrap">{l.budget || "—"}</td>
              <td className="px-4 py-4 align-top">
                <select
                  value={l.status}
                  onChange={(e) => onStatus(l.id, e.target.value)}
                  className="border border-[var(--m-border)] bg-[var(--m-bg)] px-2 py-1.5 text-xs"
                  data-testid={`lead-status-${l.id}`}
                >
                  {STATUSES.map((s) => <option key={s} value={s}>{s.toUpperCase()}</option>)}
                </select>
                <div className="mt-2"><StatusBadge s={l.status} /></div>
              </td>
              <td className="px-4 py-4 align-top text-xs text-[var(--m-ink-3)] whitespace-nowrap">
                {new Date(l.created_at).toLocaleDateString()} <br />
                {new Date(l.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              </td>
              <td className="px-4 py-4 align-top">
                <button onClick={() => onDelete(l.id)} className="p-2 text-[var(--m-error)] hover:bg-[var(--m-error)] hover:text-white transition-colors" data-testid={`lead-delete-${l.id}`}>
                  <Trash2 size={14} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const ContactsTable = ({ contacts, onDelete }) => {
  if (contacts.length === 0) {
    return <div className="bg-[var(--m-bg)] border border-[var(--m-border)] p-20 text-center text-[var(--m-ink-3)]">No contact messages yet.</div>;
  }
  return (
    <div className="bg-[var(--m-bg)] border border-[var(--m-border)] overflow-x-auto" data-testid="admin-contacts-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[var(--m-border)] text-left">
            {["Name", "Contact", "Subject", "Message", "When", ""].map((h) => (
              <th key={h} className="px-4 py-4 text-[10px] tracking-[0.18em] uppercase text-[var(--m-ink-3)] font-medium">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {contacts.map((c) => (
            <tr key={c.id} className="border-b border-[var(--m-border)] hover:bg-[var(--m-bg-2)]" data-testid={`contact-row-${c.id}`}>
              <td className="px-4 py-4 font-semibold align-top">{c.name}</td>
              <td className="px-4 py-4 align-top whitespace-nowrap">
                <a href={`tel:${c.phone}`} className="flex items-center gap-1"><Phone size={11} /> {c.phone}</a>
                {c.email && <div className="text-xs text-[var(--m-ink-3)] mt-1">{c.email}</div>}
              </td>
              <td className="px-4 py-4 align-top">{c.subject || "—"}</td>
              <td className="px-4 py-4 align-top max-w-md text-[var(--m-ink-2)]">{c.message}</td>
              <td className="px-4 py-4 align-top text-xs text-[var(--m-ink-3)] whitespace-nowrap">
                {new Date(c.created_at).toLocaleDateString()}<br />
                {new Date(c.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              </td>
              <td className="px-4 py-4 align-top">
                <button onClick={() => onDelete(c.id)} className="p-2 text-[var(--m-error)] hover:bg-[var(--m-error)] hover:text-white" data-testid={`contact-delete-${c.id}`}>
                  <Trash2 size={14} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
